import { Controller, FormProvider, useForm } from "react-hook-form";

import {
  DateRange,
  DateRangePicker,
  PredefinedDateRange,
} from "src/base-components/DateRangePicker";
import { RequiredAsterisk } from "src/base-components/RequiredAsterisk";
import { BackfillQueryRequestEnvironmentEnum } from "src/clients/features-control";
import { Button } from "src/design-system/Button";
import { Modal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import { useBackfillFeatureQuery } from "src/featureCatalogue/queries";
import { Query } from "src/featureCatalogue/utils";
import { useEnvironment } from "src/hooks/useEnvironment";
import { logger } from "src/utils/logger";
import { toastError } from "src/utils/toastError";

export type BackfillQueryForm = {
  id: string;
  timestamp: DateRange | undefined;
};

type Props = {
  afterLeave: () => void;
  onClose: () => void;
  query: Query | undefined;
  open: boolean;
};

export const BackfillQueryModal: React.FC<Props> = ({
  open,
  query,
  afterLeave,
  onClose,
}) => {
  const { mutateAsync: backfillQuery } = useBackfillFeatureQuery();
  const [env] = useEnvironment();
  const form = useForm<BackfillQueryForm>({
    values: {
      timestamp: undefined,
      id: query?.id ?? "",
    },
  });

  const handleSubmit = async (values: BackfillQueryForm) => {
    try {
      const response = await backfillQuery({
        ...values,
        env: env as BackfillQueryRequestEnvironmentEnum,
      });
      const message = `Backfill started with process id ${response.data.execution_id}`;

      toastActions.success({ title: message });
      logger.log(message);
      onClose();
    } catch (e) {
      toastError(e);
      logger.error(e);
    }
  };

  return (
    <Modal
      afterLeave={() => {
        afterLeave();
        form.reset();
      }}
      open={open}
      size="sm"
      onClose={onClose}
    >
      <Modal.Header>Backfill historical feature values</Modal.Header>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <FormProvider {...form}>
          <Modal.Content>
            <div className="mb-6">
              <label className="font-inter-semibold-13px text-gray-800">
                Time range <RequiredAsterisk />
              </label>
              <p className="mb-2 font-inter-normal-12px text-gray-500">
                Select a date range (max 100 days). Backfill will run for all
                closed windows that end within this period.
              </p>
              <div>
                <Controller
                  name="timestamp"
                  render={({ field }) => (
                    <DateRangePicker
                      minRangeLimitInDays={2}
                      placeholder="Select a time range"
                      predefinedDateOptions={[
                        PredefinedDateRange.LAST_24_HOURS,
                        PredefinedDateRange.LAST_7_DAYS,
                        PredefinedDateRange.LAST_30_DAYS,
                        PredefinedDateRange.LAST_100_DAYS,
                      ]}
                      rangeLimitInDays={100}
                      value={field.value}
                      usePredefinedDates
                      onChange={field.onChange}
                    />
                  )}
                  rules={{ required: true }}
                />
              </div>
            </div>
          </Modal.Content>
          <Modal.Footer
            primaryButton={
              <Button
                isDisabled={!form.formState.isValid}
                htmlType="submit"
                isLoading={form.formState.isSubmitting}
                variant="primary"
              >
                Backfill feature
              </Button>
            }
          />
        </FormProvider>
      </form>
    </Modal>
  );
};
